import { HomepageContent } from '@/app/(site)/(home)/v2/lib/fetch-content';
import { sanityImageUrlFor } from '@/content/sanity/sanity-image-url-for/sanity-image-url-for';
import {
  List,
  ListItem,
  Stack,
  StackProps,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PortableText } from '@portabletext/react';
import Image from 'next/image';
import Link from 'next/link';
import { useExperienceCategory } from '../../../../hooks/use-experience-category';

/**
 * Sample Data
 */

const sampleData = [
  {
    id: '1',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/1.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-01T10:00:00.000Z',
    title: 'Sample Experience 1',
    host: {
      profile: {
        nickname: 'Host1',
      },
    },
    content: {
      description: 'Description for Sample Experience 1',
    },
  },
  {
    id: '2',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/2.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-02T11:00:00.000Z',
    title: 'Sample Experience 2',
    host: {
      profile: {
        nickname: 'Host2',
      },
    },
    content: {
      description: 'Description for Sample Experience 2',
    },
  },
  {
    id: '3',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/3.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-03T12:00:00.000Z',
    title: 'Sample Experience 3',
    host: {
      profile: {
        nickname: 'Host3',
      },
    },
    content: {
      description: 'Description for Sample Experience 3',
    },
  },
  {
    id: '4',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/4.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-04T13:00:00.000Z',
    title: 'Sample Experience 4',
    host: {
      profile: {
        nickname: 'Host4',
      },
    },
    content: {
      description: 'Description for Sample Experience 4',
    },
  },
  {
    id: '5',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/5.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-05T14:00:00.000Z',
    title: 'Sample Experience 5',
    host: {
      profile: {
        nickname: 'Host5',
      },
    },
    content: {
      description: 'Description for Sample Experience 5',
    },
  },
  {
    id: '1',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/1.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-01T10:00:00.000Z',
    title: 'Sample Experience 1',
    host: {
      profile: {
        nickname: 'Host1',
      },
    },
    content: {
      description: 'Description for Sample Experience 1',
    },
  },
  {
    id: '2',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/2.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-02T11:00:00.000Z',
    title: 'Sample Experience 2',
    host: {
      profile: {
        nickname: 'Host2',
      },
    },
    content: {
      description: 'Description for Sample Experience 2',
    },
  },
  {
    id: '3',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/3.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-03T12:00:00.000Z',
    title: 'Sample Experience 3',
    host: {
      profile: {
        nickname: 'Host3',
      },
    },
    content: {
      description: 'Description for Sample Experience 3',
    },
  },
  {
    id: '4',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/4.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-04T13:00:00.000Z',
    title: 'Sample Experience 4',
    host: {
      profile: {
        nickname: 'Host4',
      },
    },
    content: {
      description: 'Description for Sample Experience 4',
    },
  },
  {
    id: '5',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/5.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-05T14:00:00.000Z',
    title: 'Sample Experience 5',
    host: {
      profile: {
        nickname: 'Host5',
      },
    },
    content: {
      description: 'Description for Sample Experience 5',
    },
  },
];

/**
 * END Sample Data
 */

const CardLink = styled(Link)`
  text-decoration: none;
  border-radius: 20px;
  outline: 2px solid transparent;
  transition: outline 0.5s;

  &:hover,
  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
    outline-offset: 4px;
  }
`;

const CardWrapper = styled((props: StackProps) => (
  <Stack {...props} p={2} gap={2} direction="row" />
))`
  border-radius: 20px;
  background-color: transparent;
`;

const CardTitle = styled(Typography)`
  color: white;
  font-weight: 700;
  font-size: 16px;
`;

const CardImage = styled(Image)`
  width: 97px;
  height: 97px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-right: 4px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-left: 3px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
`;

const HostedBy = styled(Typography)`
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
`;

interface FeaturedPastProps {
  homepageContent: HomepageContent;
}

export function FeaturedPast({ homepageContent }: FeaturedPastProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { category } = useExperienceCategory();
  const { featuredPastExperiences = [] } = homepageContent || {};
  const filteredFeaturedPastExperiences = featuredPastExperiences.filter(
    exp => exp.category === category,
  );

  if (!filteredFeaturedPastExperiences.length) {
    return null;
  }

  return (
    <Stack gap={2} padding={2}>
      {filteredFeaturedPastExperiences.map(({ host, mainImage, shortDescription, title }) => {
        const description = (
          <Typography component="div" color="white">
            <PortableText
              value={shortDescription}
              components={{
                block: {
                  h1: ({ children }) => <Typography variant="h1">{children}</Typography>,
                  h2: ({ children }) => <Typography variant="h2">{children}</Typography>,
                  h3: ({ children }) => <Typography variant="h3">{children}</Typography>,
                  h4: ({ children }) => <Typography variant="h4">{children}</Typography>,
                  h5: ({ children }) => <Typography variant="h5">{children}</Typography>,
                  h6: ({ children }) => <Typography variant="h6">{children}</Typography>,
                  normal: ({ children }) => <Typography>{children}</Typography>,
                },
                list: ({ children }) => <List>{children}</List>,
                listItem: ({ children }) => <ListItem>{children}</ListItem>,
              }}
            />
          </Typography>
        );
        return (
          <CardWrapper key={title} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            <Stack direction="row" gap={2}>
              <CardImage
                src={sanityImageUrlFor(mainImage).width(800).height(800).url()}
                alt=""
                width={200}
                height={200}
              />
              <Stack gap={1}>
                <CardTitle>{title}</CardTitle>
                <HostedBy>Hosted by {host}</HostedBy>
                {!isMobile && description}
              </Stack>
            </Stack>
            {isMobile && description}
          </CardWrapper>
        );
      })}
    </Stack>
  );
}
